import React, { useState, useContext, useEffect } from 'react';
import { Masonry } from '@mui/lab';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import {
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Divider,
  StepIconProps,
  styled,
} from '@mui/material';
import Check from '@mui/icons-material/Check';
import {
  isMobile,
  isDesktop,
  isIpad,
  generateWidthByscreenSize,
} from '../../global/ScreenSizeHelper';
import { StoreHeaderDataContext } from '../../context-api/StoreHeaderDataProvider';
import { OnTheMenuDataContext } from '../../context-api/OnTheMenuDataProvider';
import { GlobalStateDataContext } from '../../context-api/GlobalStateDataProvider';
import imageUrlTransformation from '../../global/imageUrlTransformation';
import fallBackImage from '../../global/assets/svg/ImageError.svg';

import { LoginDataContext } from '../../context-api/LoginDataProvider';
import { SpecialMarkingIcons } from '../../global/assets/Icons';
import Avatar from '@mui/material/Avatar';
import AtomicCircleButton from '../../atomic/AtomicCircleButton/AtomicCircleButton';

// import "./PromoProduct.scss";

// import "./PromotionalKitchenMenu.scss";
import { CheckoutDataContext } from '../../context-api/CheckoutDataProvider';
import Loader from '../../composed/Loader/Loader';
import {
  ButtonTabSkeleton,
  ProductCardSkeleton,
} from '../../composed/ProductCard/ProductCardSkeleton';
import { QuantityPicker } from '../../atomic/QuantityPicker/QuantityPicker';
import './ProductCardStyle.scss';

export const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#fff',
    color: '#000',
    maxWidth: 160,
    fontSize: theme.typography.pxToRem(14),
    border: '1px solid #dadde9',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#fff',
  },
}));

const navyBlue = '#001F3F';

// Custom Step Icon to make completed and active steps navy blue
function CustomStepIcon(props) {
  const { active, completed, icon } = props;
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        backgroundColor: completed || active ? navyBlue : '#ccc',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
      }}
    >
      {completed ? <Check fontSize='small' /> : icon}
    </Box>
  );
}

const VerticalAlwaysOpenStepper = () => {
  const { kitchensByZipcodeResponse } = useContext(StoreHeaderDataContext) || {};
  const { isLoggedIn, customerInfoData } = useContext(LoginDataContext);
  const { setDiscountCodeValidationData, setPromoCodeOrderSummary } =
    useContext(CheckoutDataContext);
  const {
    storeName,
    productsData,
    setProductsData,
    setStoreName,
    getProductList,
    storeAccountType,
    sortedKitchenData,
    promotionalKitchenList,
    cartItems,
    setCartItems,
    PromoSpendRequiredAmount,
    subtotalInCart,
    HowMuchMoreToSpend,
    PromoVerbage,
    Qty,
    setQty,
  } = useContext(OnTheMenuDataContext);
  const [snackbar, setSnackbar] = useState({
    show: false,
    message: null,
  });
  const { onLoadInfoResponse } = useContext(GlobalStateDataContext);
  const { loading, data: custData } = customerInfoData;
  const { data, errors, isLoading } = productsData;
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const promoPermittedItemsQty =
    onLoadInfoResponse?.data?.data?.data?.operationFlags?.permittedComboPromoItemQty * Qty; // Very important variable which will define how many promotional items can be added to basket
  const baseMealPrice = onLoadInfoResponse?.data?.data?.data?.operationFlags?.nextday_meal_price;

  const AccountTypeSubscription = () => custData?.data?.user_Info?.account_type === 'Subscription';

  const AccountTypePayAsYouGo = () => custData?.data?.user_Info?.account_type === 'payAsYouGo';

  const PromoAddTermCondition = (product) => {
    if (!isLoggedIn) {
      return {
        ToolTipLabel: 'Please login first',
        disableButton: true,
      };
    }

    let inCartProductQuantity = 0;
    cartItems?.promotionalItems?.forEach((item) => {
      if (product.product_sku === item.product_sku) {
        inCartProductQuantity += item.selectedQuantity;
      }
    });
    const howMuchtoSpend = HowMuchMoreToSpend();
    const BranchFlag = onLoadInfoResponse?.data?.data?.data?.operationFlags;
    const SubscriptionComboProduct =
      product?.product_category === 'subscription_combo' ||
      product?.product_category === 'promo_side_one' ||
      product?.product_category === 'promo_side_two';
    const disableTooltipMsg =
      [product.product_category] >= promoPermittedItemsQty ||
      product.available_quantity - inCartProductQuantity <= 0;

    if (AccountTypeSubscription()) {
      const qtyCheck = inCartQuantity[product.product_category] >= promoPermittedItemsQty;
      if (qtyCheck) {
        return {
          ToolTipLabel: !disableTooltipMsg && 'Exceeds permissible qty',
          disableButton: qtyCheck,
        };
      }

      if (
        BranchFlag?.branchAgreedFreeComboPromoForSubscription &&
        sortedKitchenData?.agreedForFreeComboPromo
      ) {
        return {
          ToolTipLabel: !disableTooltipMsg
            ? SubscriptionComboProduct
              ? `Add one combo item  (OR) Minimum spend $${howMuchtoSpend} on regular menu to qualify`
              : `Minimum spend $${howMuchtoSpend} on regular menu to qualify`
            : '',
          disableButton:
            (SubscriptionComboProduct && cartItems?.mealItems?.length > 0) ||
            subtotalInCart >= sortedKitchenData?.amountRequiredForComboPromo
              ? false
              : true,
        };
      }

      if (
        BranchFlag?.branchAgreedFreeComboPromoForPayAsYouGo &&
        sortedKitchenData?.agreedForFreeComboPromo
      ) {
        return {
          ToolTipLabel:
            !disableTooltipMsg && `Minimum spend $${howMuchtoSpend} on regular menu to qualify`,
          disableButton:
            subtotalInCart >= sortedKitchenData?.amountRequiredForComboPromo ||
            subtotalInCart >= BranchFlag?.amountRequiredForComboPromo
              ? false
              : true,
        };
      }

      if (
        BranchFlag?.branchAgreedFreeComboPromoForSubscription &&
        BranchFlag?.branchAgreedFreeComboPromoForPayAsYouGo
      ) {
        return {
          ToolTipLabel: !disableTooltipMsg
            ? SubscriptionComboProduct
              ? `Add one combo item (OR) Minimum spend $${howMuchtoSpend} on regular menu to qualify`
              : `Minimum spend $${howMuchtoSpend} on regular menu to qualify`
            : '',
          disableButton:
            (SubscriptionComboProduct && cartItems?.mealItems?.length > 0) ||
            subtotalInCart >= BranchFlag?.amountRequiredForComboPromo
              ? false
              : true,
        };
      }

      if (BranchFlag?.branchAgreedFreeComboPromoForSubscription) {
        return {
          ToolTipLabel: !disableTooltipMsg
            ? SubscriptionComboProduct
              ? `Add one combo item to qualify`
              : 'None'
            : '',
          disableButton: cartItems?.mealItems?.length > 0 ? false : true,
        };
      }

      if (
        BranchFlag?.branchAgreedFreeComboPromoForPayAsYouGo ||
        sortedKitchenData?.agreedForFreeComboPromo
      ) {
        return {
          ToolTipLabel:
            !disableTooltipMsg && `Minimum spend $${howMuchtoSpend} on regular menu to qualify`,
          disableButton:
            subtotalInCart >= sortedKitchenData?.amountRequiredForComboPromo ||
            subtotalInCart >= BranchFlag?.amountRequiredForComboPromo
              ? false
              : true,
        };
      }
    }

    if (AccountTypePayAsYouGo()) {
      if (
        (BranchFlag?.branchAgreedFreeComboPromoForSubscription &&
          sortedKitchenData?.agreedForFreeComboPromo) ||
        (BranchFlag?.branchAgreedFreeComboPromoForPayAsYouGo &&
          sortedKitchenData?.agreedForFreeComboPromo) ||
        (BranchFlag?.branchAgreedFreeComboPromoForSubscription &&
          BranchFlag?.branchAgreedFreeComboPromoForPayAsYouGo) ||
        BranchFlag?.branchAgreedFreeComboPromoForSubscription ||
        BranchFlag?.branchAgreedFreeComboPromoForPayAsYouGo ||
        sortedKitchenData?.agreedForFreeComboPromo
      ) {
        return {
          ToolTipLabel:
            !disableTooltipMsg && `Minimum spend $${howMuchtoSpend} on regular menu to qualify`,
          disableButton:
            subtotalInCart >= sortedKitchenData?.amountRequiredForComboPromo ||
            subtotalInCart >= BranchFlag?.amountRequiredForComboPromo
              ? false
              : true,
        };
      }
    }

    return {
      disableButton: false,
    };
  };

  let inCartQuantity = {
    subscription_combo: 0,
    promo_side_one: 0,
    promo_side_two: 0,
  };

  cartItems?.promotionalItems?.forEach((item) => {
    inCartQuantity[item.product_category] += item.selectedQuantity;
  });

  const promoKtchenName = () =>
    kitchensByZipcodeResponse?.data?.promoKitchenNames?.[0] ||
    promotionalKitchenList?.[0]?.kitchenName;

  const PromoProductCount = data?.data?.[promoKtchenName()]?.product_count === 0;

  const PromoProducts = PromoProductCount ? null : data?.data?.[promoKtchenName()]; // Sorting kitchen information from /GetProduct API

  const withAvailableQuantity = (productsArray) =>
    productsArray.map((item) => ({
      ...item,
      disabled:
        item.available_quantity -
          (cartItems?.promotionalItems?.find((obj) => obj.product_sku === item.product_sku)
            ?.selectedQuantity || 0) <=
          0 ||
        inCartQuantity[item.product_category] >= promoPermittedItemsQty ||
        PromoAddTermCondition(item).disableButton,
    }));

  const PayAsYouGoCombo_Items = withAvailableQuantity(PromoProducts?.payasyougo_combo || []);
  const SubscriptionCombo_Items = withAvailableQuantity(PromoProducts?.subscription_combo || []);
  const SideOne_Items = withAvailableQuantity(PromoProducts?.promo_side_one || []);
  const SideTwo_Items = withAvailableQuantity(PromoProducts?.promo_side_two || []);

  useEffect(() => {
    if (PromoProducts === undefined) {
      getProductList({ kitchen_names: [promoKtchenName()] });
    }
  }, [PromoProducts]);

  // Step data
  const stepItems = {
    'Main Course': SubscriptionCombo_Items,
    'Side One': SideOne_Items,
    'Side Two': SideTwo_Items,
  };

  const stepsLabelMapping = {
    'Main Course': 'subscription_combo',
    'Side One': 'promo_side_one',
    'Side Two': 'promo_side_two',
  };

  const steps = Object.keys(stepItems);

  const onImageLoad = () => {
    setImageLoaded(true);
  };

  const onImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  const setDisabledAddToCartBtn_PromotionalItem = (product) => {
    let payAsYouGoItems = PayAsYouGoCombo_Items;
    let subscriptionItems = SubscriptionCombo_Items;
    const productCategory = product?.product_category;
    if (productCategory === 'payasyougo_combo') {
      payAsYouGoItems = PayAsYouGoCombo_Items.map((item) => {
        if (item.product_sku === product.product_sku) {
          return {
            ...item,
            disabled: true,
          };
        }
        return item;
      });
    } else if (productCategory === 'subscription_combo') {
      subscriptionItems = SubscriptionCombo_Items.map((item) => {
        if (item.product_sku === product.product_sku) {
          return {
            ...item,
            disabled: true,
          };
        }
        return item;
      });
    }

    const promoKitchenName = promoKtchenName();
    setProductsData({
      ...productsData,
      data: {
        ...productsData?.data,
        data: {
          ...productsData?.data?.data,
          [promoKitchenName]: {
            ...productsData?.data?.data?.[promoKitchenName],
            payasyougo_combo: payAsYouGoItems,
            subscription_combo: subscriptionItems,
          },
        },
      },
    });
  };

  const setDisabledAddToCartBtn_PromotionalItems = () => {
    const payAsYouGoItems = PayAsYouGoCombo_Items.map((item) => ({
      ...item,
      disabled: true,
    }));

    const subscriptionItems = SubscriptionCombo_Items.map((item) => ({
      ...item,
      disabled: true,
    }));

    const promoKitchenName = promoKtchenName();
    setProductsData({
      ...productsData,
      data: {
        ...productsData?.data,
        data: {
          ...productsData?.data?.data,
          [promoKitchenName]: {
            ...productsData?.data?.data?.[promoKitchenName],
            payasyougo_combo: payAsYouGoItems,
            subscription_combo: subscriptionItems,
          },
        },
      },
    });
  };

  const renderAddButton = (product) => (
    <div>
      <AtomicCircleButton
        style={{ marginLeft: '5px' }}
        onClick={() => {
          if (isLoggedIn && product.available_quantity > 0) {
            setDiscountCodeValidationData({
              loading: false,
              data: null,
              errorMessage: '',
              success: null,
            });
            setPromoCodeOrderSummary({});

            let inCartProductQuantity = 0;
            cartItems?.promotionalItems?.forEach((item) => {
              if (product.product_sku === item.product_sku) {
                inCartProductQuantity += item.selectedQuantity;
              }
            });

            if (inCartQuantity[product.product_category] >= promoPermittedItemsQty) {
              return;
            }

            let newCartItems = { ...cartItems };

            if (!newCartItems?.promotionalItems) {
              newCartItems.promotionalItems = [];
            }

            const productInCartIndex = newCartItems.promotionalItems?.findIndex(
              (item) =>
                item.product_name === product.product_name &&
                item.product_category === product.product_category
            );
            if (productInCartIndex > -1) {
              newCartItems.promotionalItems[productInCartIndex].selectedQuantity += 1;
            } else {
              newCartItems.promotionalItems.push({
                product_name: product.product_name,
                selectedQuantity: 1,
                available_quantity: product.available_quantity,
                product_category: product.product_category,
                product_sku: product.product_sku,
                kitchen_name: product.kitchen_name,
                kitchen_id: promotionalKitchenList?.[0]?._id,
              });
            }

            setSnackbar({
              show: true,
              message: `Promotional item ${product.product_name} added to cart`,
            });

            setCartItems({ ...newCartItems });

            if (product.available_quantity - (inCartProductQuantity + 1) === 0) {
              setDisabledAddToCartBtn_PromotionalItem(product);
            }

            if (inCartQuantity[product.product_category] + 1 >= promoPermittedItemsQty) {
              setDisabledAddToCartBtn_PromotionalItems();
            }

            return;
          }
        }}
        // disabled={checkUrlPath ? product.disabled || !promotionAvailable : true}
        disabled={product.disabled}
      />
    </div>
  );

  const productsAtThisStepNumber = (stepLabel, newCartItems) => {
    const products =
      newCartItems?.promotionalItems?.filter(
        (item) => item.product_category === stepsLabelMapping[stepLabel]
      ) || [];
    let number = 0;
    products.forEach((item) => {
      number += item.selectedQuantity;
    });
    return number;
  };

  const getProductCount = (productName) => {
    return (
      cartItems?.promotionalItems?.find((item) => {
        return item.product_name === productName;
      })?.selectedQuantity || 0
    );
  };

  if (isLoading && loading) {
    if (isDesktop || isIpad) {
      return (
        <div>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <ButtonTabSkeleton />
          </div>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            {[1, 2, 3].map((n) => (
              <ProductCardSkeleton key={n} />
            ))}
          </div>
        </div>
      );
    }
    if (isMobile) {
      return (
        <>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <ButtonTabSkeleton />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {[1, 2, 3].map((n) => (
              <ProductCardSkeleton key={n} />
            ))}
          </div>
        </>
      );
    }
  }

  return (
    <Box sx={{ maxWidth: 1024, mx: 'auto', p: 2 }}>
      <Stepper
        orientation='vertical'
        nonLinear
        activeStep={-1}
        sx={{
          '& .MuiStepConnector-line': {
            minHeight: '0px', // reduce length of vertical lines
          },
          '& .MuiStep-root': {
            paddingBottom: 0, // reduce step spacing
          },
          '& .MuiStep-vertical > div': {
            marginLeft: '8px',
            borderLeft: '1px solid lightgray',
          },
          '& .MuiStepLabel-root': {
            marginBottom: 0, // tighten label layout
          },
        }}
      >
        <div className='qunty-style'>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'white',
              marginBottom: '15px',
              marginRight: '5px',
              fontFamily: 'Inter Medium',
              borderRadius: '5px',
              height: '35px',
              width: '160px',
              fontSize: '20px',
              color: '#FF893A',
              // border: '2px solid #FF893A'
            }}
          >
            NextDay Meal
          </div>

          <div
            style={{
              marginBottom: '15px',
              marginRight: '10px',
              fontFamily: 'Inter Medium',
              fontSize: '16px',
              color: 'rgb(0, 49, 92)',
            }}
          >
            {' '}
            Qty{' '}
          </div>
          <QuantityPicker
            onQuantityChange={(qty) => {
              if (qty > 0) {
                setQty(qty);

                if (qty < Qty) {
                  let newCartItems = { ...cartItems };
                  newCartItems.promotionalItems = [];
                  setCartItems({ ...newCartItems });
                }
              }
            }}
            quantity={Qty}
          />
          <div
            style={{
              marginBottom: '15px',
              marginLeft: '5px',
              fontFamily: 'Inter Medium',
              fontSize: '18px',
              color: 'rgb(0, 49, 92)',
            }}
          >
            {' '}
            ${Qty * baseMealPrice}{' '}
          </div>
        </div>

        {steps.map((stepLabel, index) => (
          <Step
            key={stepLabel}
            expanded
            completed={productsAtThisStepNumber(stepLabel, cartItems) > 0}
          >
            <StepLabel
              StepIconComponent={({ completed, active }) =>
                CustomStepIcon({ completed, icon: index + 1, active })
              }
              optional={
                productsAtThisStepNumber(stepLabel, cartItems) > 0 ? (
                  <Typography variant='caption' color={navyBlue}>
                    {`${productsAtThisStepNumber(stepLabel, cartItems)} selected`}
                  </Typography>
                ) : null
              }
              sx={{
                '& .MuiStepLabel-label': {
                  color: productsAtThisStepNumber(stepLabel, cartItems) > 0 ? '#181436' : 'inherit',
                },
              }}
            >
              <Typography variant='h6'>
                {`${stepLabel} (Choose ${promoPermittedItemsQty})`}
              </Typography>
            </StepLabel>

            <Masonry
              sx={{ width: isIpad ? '80vw' : '100%', padding: '0px 10px 0px 10px' }}
              columns={isMobile ? 2 : 4}
              spacing={1}
            >
              {(stepItems[stepLabel] || []).map((el) => {
                const productCount = getProductCount(el.product_name);
                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid #ECECEC',
                      minWidth: '150px',
                      borderRadius: '5px',
                      overflow: 'hidden',
                      display: 'flex',
                      margin: '5px',
                    }}
                    key={el.product_name}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: el.image_url === null ? '70%' : null,
                        border: '1px solid #ECECEC',
                        minWidth: '150px',
                        overflow: 'hidden',
                        borderRadius: '5px',
                        overflow: 'hidden',
                        display: 'flex',
                        // objectFit: 'cover',
                        // cursor: 'pointer',
                        position: 'relative',
                      }}
                    >
                      {productCount > 0 && (
                        <div
                          style={{
                            zIndex: 1,
                            right: '8px',
                            top: '8px',
                            position: 'absolute',
                            background: '#E0E0E0',
                            color: '#181436',
                            padding: '5px',
                            width: '20px',
                            borderRadius: '15px',
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'center',
                            fontSize: '14px',
                          }}
                        >
                          {' '}
                          {productCount}
                        </div>
                      )}
                      <img
                        width={imageLoaded ? '100%' : 0}
                        src={
                          imageError
                            ? fallBackImage
                            : imageUrlTransformation({
                                imageUrl: el.image_url,
                                fillType: 'fill',
                                width: 240,
                              })
                        }
                        onLoad={() => onImageLoad()}
                        onError={() => onImageError()}
                        onClick={() => {
                          console.log('-----nextday meal image clicked');
                        }}
                      />
                      {!imageLoaded && <Loader color='black' width='20px' height='20px' />}

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          height: 'auto',
                          padding: '10px 10px 10px 15px',
                        }}
                      >
                        <div style={{ width: '78%' }}>
                          <div
                            style={{
                              fontSize: '14px',
                              fontFamily: 'Roboto Medium',
                              color: '#181436',
                              letterSpacing: '0.5px',
                              textTransform: 'capitalize',
                            }}
                          >
                            {el?.product_name}
                          </div>
                          <div
                            style={{
                              fontSize: '13px',
                              fontFamily: 'Roboto Light',
                              color: '#A8A4BF',
                            }}
                          >
                            {' '}
                            {el?.recipe_with}
                          </div>

                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              marginTop: '15px',
                            }}
                          >
                            {el?.special_marking?.map((g, index) => {
                              return (
                                <Avatar
                                  alt={g}
                                  src={SpecialMarkingIcons[g]}
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    marginRight: '5px',
                                  }}
                                  key={`${index}_specialmarking`}
                                />
                              );
                            })}
                          </div>
                        </div>

                        <div style={{ position: 'relative', top: '-16px' }}>
                          {el.disabled || !isLoggedIn ? (
                            <CustomTooltip
                              title={PromoAddTermCondition(el).ToolTipLabel}
                              placement='top'
                              arrow={true}
                              enterTouchDelay={0}
                              PopperProps={{
                                disablePortal: true,
                              }}
                            >
                              {renderAddButton(el)}
                            </CustomTooltip>
                          ) : (
                            renderAddButton(el)
                          )}
                          {/* 
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            fontSize: "14px",
                                                            fontFamily: "Roboto Bold",
                                                            color: "#33A754",
                                                            marginTop: "5px",
                                                            background: "rgb(149, 237, 135)",
                                                            padding: "5px",
                                                            borderRadius: "5px",
                                                        }}
                                                    >
                                                        Free
                                                    </div> */}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Masonry>
            {index < steps.length - 1 && <Divider sx={{ my: 2 }} />}
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default VerticalAlwaysOpenStepper;
